import { ListingType, PreservedListingOptions, SgwForm, SHOPGOODWILL_CHANNEL_ID } from './types'
import { formatMoneyForInput } from '../../utils'
import { CategoryListingConfiguration } from '../../types/api'

// LATER remove. I will make use of this for runtime assertions during dev
/* eslint-disable no-throw-literal */

interface Action extends Record<string, any> {
  type: string
}

const applyPreservedListingOptions = <T extends Record<string, unknown>>(
  form: T,
  preservedListingOptions?: PreservedListingOptions
): T => {
  if (!preservedListingOptions) {
    return form
  }

  const preservedValues: PreservedListingOptions = {}

  if (preservedListingOptions.noCombineShipping !== undefined) {
    preservedValues.noCombineShipping = preservedListingOptions.noCombineShipping
  }

  if (preservedListingOptions.itemFeatured !== undefined) {
    preservedValues.itemFeatured = preservedListingOptions.itemFeatured
  }

  if (Object.keys(preservedValues).length === 0) {
    return form
  }

  return {
    ...form,
    ...preservedValues
  }
}

export const sgwFormReducer = (sgwForm, action: Action): SgwForm => {
  switch (action.type) {
    case 'clearForm': {
      return applyPreservedListingOptions({}, action.preservedListingOptions)
    }
    case 'clearStrategyForm': {
      const { noCombineShipping, itemFeatured } = sgwForm

      return applyPreservedListingOptions(
        {
          oldCategoryConfig: sgwForm.oldCategoryConfig,
          ...sgwForm.oldCategoryConfig
        },
        { noCombineShipping, itemFeatured }
      )
    }
    case 'onValueChange': {
      const { newValues } = action

      return {
        ...sgwForm,
        ...newValues
      }
    }
    case 'categoryConfigLoaded': {
      // This is where admin market settings are applied
      const { cmcId } = action

      // types are good
      // eslint-disable-next-line prefer-destructuring
      const resData: CategoryListingConfiguration = action.resData

      const categoryConfig = {
        // We could grab default carrier from props.current.organization.settings.carrierId or API::V5::ListingConfigurationController.
        // We're using the latter since it already decides where the default carrier comes from. But either could work honestly.
        carrierId: resData.defaultCarrierId || 2, // Fedex is a good default choice
        // Apply market default settings before we apply the listing strategy configuration,
        // so that the listing strategy values will take preference.
        bidIncrement: resData.marketSettings?.shopgoodwill?.defaultBidIncrement || '1.0',
        price: formatMoneyForInput(resData.marketSettings?.shopgoodwill?.defaultStartingPrice) || '',
        shippingPrice: formatMoneyForInput(resData.marketSettings?.shopgoodwill?.defaultShippingPrice),
        reservePrice: '0.0', // admin channel settings don't allow configuring this
        handlingPrice: resData.marketSettings?.shopgoodwill?.defaultHandlingPrice || '0.0',
        categoryMarketCategoryId: cmcId,
        auctionDuration: String(resData.marketSettings?.shopgoodwill?.defaultAuctionDuration || 7),
        noCombineShipping: resData.marketSettings?.shopgoodwill?.defaultNoCombineShipping,
        itemFeatured: false
        // auctionStart is not handled. Literally nobody configures this to a non-zero value. Schema has a default of zero.
        // Settings::Markets::Shopgoodwill.where(owner: Organization.where("name ilike '%goodwill%'")).pluck(:default_auction_start).tally
      }

      return applyPreservedListingOptions(
        {
          ...sgwForm,

          // When we dispatch clearStrategyForm, we want to remove whatever values the strategy applied BUT keep the category defaults.
          // Storing this allows us to reset the form state (keeping the category/admin setting defaults)
          oldCategoryConfig: categoryConfig, // don't serialize this

          ...categoryConfig
        },
        action.preservedListingOptions
      )
    }
    case 'strategyConfigLoaded': {
      const { resData } = action

      const strategyConfiguration = resData.configuration
      const channelIds = strategyConfiguration.map(c => c.marketId)

      const newSgwForm = { ...sgwForm }

      if (channelIds.includes(SHOPGOODWILL_CHANNEL_ID)) {
        const sgwConfiguration = strategyConfiguration.find(c => c.marketId === SHOPGOODWILL_CHANNEL_ID)

        let sgwListingType: ListingType = 'AUCTION'

        // null means "don't show this field"
        // If strategy defines buy_now_price, show it in the details drawer. Otherwise, nothing in UI.
        let sgwBuyItNowPrice = null

        if (sgwConfiguration.stepRank !== 0) {
          throw "Patrick needs to handle this, stepRank !== 0. Shouldn't happen with SGW-only strategies"
        }

        if (sgwConfiguration.priceChangeBy === 'SET' && sgwConfiguration.priceChange) {
          // We are able to determine the starting price.
          // Possibly override the admin configuration applied earlier.
          newSgwForm.price = formatMoneyForInput(sgwConfiguration.priceChange)
        }

        if (sgwConfiguration.buyItNowPriceChangeBy === 'SET' && sgwConfiguration.buyItNowPriceChange) {
          if (sgwConfiguration.priceChange === sgwConfiguration.buyItNowPriceChange) {
            // Our SGW model conveys fixed price like this.
            // The listing strategy edit page requires users to fill it out this way (similar to the classic listing flow).
            sgwListingType = 'FIXED_PRICE'
          } else {
            // We will show them the "buy it now" price (which is higher than the starting bid) in the details drawer
            sgwBuyItNowPrice = formatMoneyForInput(sgwConfiguration.buyItNowPriceChange)
          }
        }

        if (sgwConfiguration.shippingPriceChangeBy === 'SET' && sgwConfiguration.shippingPriceChange) {
          newSgwForm.shippingPrice = formatMoneyForInput(sgwConfiguration.shippingPriceChange)
        }

        if (sgwConfiguration.reservePriceChangeBy === 'SET' && sgwConfiguration.reservePriceChange) {
          newSgwForm.reservePrice = sgwConfiguration.reservePriceChange
        }

        if (sgwConfiguration.handlingPriceChangeBy === 'SET' && sgwConfiguration.handlingPriceChange) {
          newSgwForm.handlingPrice = sgwConfiguration.handlingPriceChange
        }

        if (sgwConfiguration.listingDuration) {
          newSgwForm.auctionDuration = sgwConfiguration.listingDuration
        }

        newSgwForm.listingType = sgwListingType
        newSgwForm.buyItNowPrice = sgwBuyItNowPrice
      }

      return newSgwForm
    }
    default: {
      throw Error(`Unknown action: ${action.type}`)
    }
  }
}
